<template>
  <div class="sales-by-modifier">
    <!-- Header Section -->
    <div class="bg-dark m-b-30">
      <div class="container">
        <div class="row p-b-30 p-t-30">
          <div class="col-md-12 text-center">
            <h2 class="text-white">
              <i class="mdi mdi-chart-donut"></i> Sales by Modifier Analysis
            </h2>
            <p class="text-white opacity-75">Comprehensive modifier performance analysis and insights</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Advanced Filters Section -->
    <div class="container">
      <el-card class="m-b-20">
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-filter-variant"></i> Advanced Filters</h4>
          <el-button type="text" @click="resetFilters">
            <i class="el-icon-refresh"></i> Reset
          </el-button>
        </div>

        <el-form :inline="true" size="small" label-width="100px">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Date Range">
                <el-date-picker
                  v-model="filters.dateRange"
                  type="daterange"
                  range-separator="to"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  :shortcuts="dateShortcuts"
                  @change="handleDateChange"
                />
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Modifiers">
                <el-select
                  v-model="filters.modifier_ids"
                  multiple
                  filterable
                  placeholder="All Modifiers"
                  @change="loadModifierData"
                >
                  <el-option
                    v-for="modifier in modifiers"
                    :key="modifier.id"
                    :label="modifier.name"
                    :value="modifier.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Items">
                <el-select
                  v-model="filters.item_ids"
                  multiple
                  filterable
                  placeholder="All Items"
                  @change="loadModifierData"
                >
                  <el-option
                    v-for="item in items"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Employees">
                <el-select
                  v-model="filters.user_id"
                  multiple
                  filterable
                  placeholder="All Employees"
                  @change="loadModifierData"
                >
                  <el-option
                    v-for="user in users"
                    :key="user.id"
                    :label="user.name"
                    :value="user.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Customers">
                <el-select
                  v-model="filters.customer_id"
                  multiple
                  filterable
                  placeholder="All Customers"
                  @change="loadModifierData"
                >
                  <el-option
                    v-for="customer in customers"
                    :key="customer.id"
                    :label="customer.name"
                    :value="customer.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Payment Methods">
                <el-select
                  v-model="filters.payment_method_id"
                  multiple
                  filterable
                  placeholder="All Payment Methods"
                  @change="loadModifierData"
                >
                  <el-option
                    v-for="method in paymentMethods"
                    :key="method.id"
                    :label="method.name"
                    :value="method.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Analysis Type">
                <el-select
                  v-model="filters.analysis_type"
                  placeholder="Analysis Type"
                  @change="loadModifierData"
                >
                  <el-option label="Revenue" value="revenue"></el-option>
                  <el-option label="Quantity" value="quantity"></el-option>
                  <el-option label="Orders" value="orders"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Sort By">
                <el-select
                  v-model="filters.sort_by"
                  placeholder="Sort by"
                  @change="loadModifierData"
                >
                  <el-option label="Revenue (High to Low)" value="revenue_desc"></el-option>
                  <el-option label="Revenue (Low to High)" value="revenue_asc"></el-option>
                  <el-option label="Quantity (High to Low)" value="quantity_desc"></el-option>
                  <el-option label="Quantity (Low to High)" value="quantity_asc"></el-option>
                  <el-option label="Orders (High to Low)" value="orders_desc"></el-option>
                  <el-option label="Orders (Low to High)" value="orders_asc"></el-option>
                  <el-option label="Modifier Name" value="name"></el-option>
                </el-select>
              </el-form-item>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="loadModifierData" :loading="loading">
                  Search
                </el-button>
                <el-button type="success" icon="el-icon-download" @click="exportData" :loading="exporting">
                  Export
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>

      <!-- Summary Statistics -->
      <el-row :gutter="20" class="m-b-20">
        <el-col :xs="24" :sm="12" :md="6" v-for="stat in summaryStats" :key="stat.title" class="m-b-20">
          <el-card :body-style="{ padding: '20px' }" class="stat-card">
            <div class="stat-content">
              <div class="stat-info">
                <h4 class="stat-title">{{ stat.title }}</h4>
                <h2 class="stat-value">{{ stat.value }}</h2>
                <div class="stat-comparison">
                  <span :class="['trend', stat.trend > 0 ? 'positive' : 'negative']">
                    <i :class="stat.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(stat.trend) }}%
                  </span>
                  <span class="comparison-text">vs previous period</span>
                </div>
              </div>
              <div class="stat-icon">
                <i :class="stat.icon"></i>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- Loading State -->
      <div v-if="loading" class="text-center p-40">
        <i class="el-icon-loading" style="font-size: 24px;"></i>
        <p>Loading modifier analysis...</p>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Charts Section -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Modifier Performance Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-bar"></i> Modifier Performance</h4>
                <el-select v-model="chartView" size="mini" @change="updateChartData">
                  <el-option label="Revenue" value="revenue"></el-option>
                  <el-option label="Quantity" value="quantity"></el-option>
                  <el-option label="Orders" value="orders"></el-option>
                </el-select>
              </div>
              <div class="chart-container">
                <bar-chart
                  v-if="modifierData.length > 0"
                  :chart-data="modifierChartData"
                  :chart-options="modifierChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-bar" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Modifier Distribution Chart -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-pie"></i> Modifier Distribution</h4>
                <el-switch v-model="showPercentage" active-text="%" inactive-text="Value"/>
              </div>
              <div class="chart-container">
                <doughnut-chart
                  v-if="modifierData.length > 0"
                  :chart-data="distributionChartData"
                  :chart-options="distributionChartOptions"
                  :height="300"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-pie" style="font-size: 48px;"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Trends and Comparison -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Modifier Trends -->
          <el-col :xs="24" :lg="16" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-line"></i> Modifier Trends</h4>
                <el-select v-model="trendPeriod" size="mini" @change="loadTrendsData">
                  <el-option label="Daily" value="daily"></el-option>
                  <el-option label="Weekly" value="weekly"></el-option>
                  <el-option label="Monthly" value="monthly"></el-option>
                </el-select>
              </div>
              <div class="chart-container">
                <line-chart
                  v-if="trendsData.length > 0"
                  :chart-data="trendsChartData"
                  :chart-options="trendsChartOptions"
                  :height="250"
                />
                <div v-else class="text-center p-40 text-muted">
                  <i class="mdi mdi-chart-line" style="font-size: 48px;"></i>
                  <p>No trends data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Top Modifiers -->
          <el-col :xs="24" :lg="8" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-trophy"></i> Top Performing Modifiers</h4>
              </div>
              <div class="top-modifiers-list">
                <div
                  v-for="(modifier, index) in topModifiers"
                  :key="modifier.modifier_id"
                  class="modifier-item"
                >
                  <div class="modifier-rank">{{ index + 1 }}</div>
                  <div class="modifier-info">
                    <div class="modifier-name">{{ modifier.modifier_name }}</div>
                    <div class="modifier-stats">
                      <span class="revenue">{{ formatCurrency(modifier.total_revenue) }}</span>
                      <span class="quantity">{{ modifier.total_quantity }} units</span>
                    </div>
                    <el-progress
                      :percentage="(modifier.total_revenue / Math.max(...topModifiers.map(m => m.total_revenue))) * 100"
                      :stroke-width="4"
                      :show-text="false"
                      :color="getProgressColor(index)"
                    />
                  </div>
                  <div class="modifier-trend">
                    <span :class="['growth', modifier.growth_rate > 0 ? 'positive' : 'negative']">
                      <i :class="modifier.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(modifier.growth_rate) }}%
                    </span>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Modifier Data Table -->
        <el-card>
          <div slot="header" class="card-header">
            <h4><i class="mdi mdi-table"></i> Modifier Analysis</h4>
            <div class="header-actions">
              <el-tag type="info">{{ pagination.total }} modifiers found</el-tag>
              <el-button-group>
                <el-button size="mini" @click="toggleViewMode">
                  <i :class="viewMode === 'table' ? 'el-icon-menu' : 'el-icon-s-grid'"></i>
                  {{ viewMode === 'table' ? 'Grid' : 'Table' }}
                </el-button>
              </el-button-group>
            </div>
          </div>

          <!-- Table View -->
          <div v-if="viewMode === 'table'">
            <el-table
              :data="modifierData"
              style="width: 100%"
              size="small"
              @sort-change="handleSortChange"
              @selection-change="handleSelectionChange"
            >
              <el-table-column type="selection" width="55" />
              <el-table-column prop="modifier_id" label="ID" width="80" />
              <el-table-column prop="modifier_name" label="Modifier Name" width="200" />
              <el-table-column label="Revenue" width="120" sortable="custom">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.total_revenue) }}
                </template>
              </el-table-column>
              <el-table-column label="Quantity" width="100" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.total_quantity.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Orders" width="100" sortable="custom">
                <template slot-scope="scope">
                  {{ scope.row.total_orders.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Avg Price" width="120">
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.average_price) }}
                </template>
              </el-table-column>
              <el-table-column label="Revenue Share" width="120">
                <template slot-scope="scope">
                  {{ scope.row.revenue_share.toFixed(1) }}%
                </template>
              </el-table-column>
              <el-table-column label="Unique Items" width="100">
                <template slot-scope="scope">
                  {{ scope.row.unique_items.toLocaleString() }}
                </template>
              </el-table-column>
              <el-table-column label="Growth" width="100">
                <template slot-scope="scope">
                  <span :class="['growth', scope.row.growth_rate > 0 ? 'positive' : 'negative']">
                    <i :class="scope.row.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(scope.row.growth_rate) }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120" fixed="right">
                <template slot-scope="scope">
                  <el-button-group>
                    <el-button
                      size="mini"
                      type="primary"
                      icon="el-icon-view"
                      @click="viewModifierDetails(scope.row)"
                    />
                    <el-button
                      size="mini"
                      type="success"
                      icon="el-icon-chart"
                      @click="viewModifierTrends(scope.row)"
                    />
                  </el-button-group>
                </template>
              </el-table-column>
            </el-table>

            <!-- Pagination -->
            <div class="pagination-container">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.current_page"
                :page-sizes="[10, 25, 50, 100]"
                :page-size="+pagination.per_page"
                layout="total, sizes, prev, pager, next, jumper"
                :total="pagination.total"
              />
            </div>
          </div>

          <!-- Grid View -->
          <div v-else class="grid-view">
            <el-row :gutter="20">
              <el-col
                v-for="modifier in modifierData"
                :key="modifier.modifier_id"
                :xs="24"
                :sm="12"
                :md="8"
                :lg="6"
                class="m-b-20"
              >
                <el-card :body-style="{ padding: '15px' }" class="modifier-card">
                  <div class="modifier-header">
                    <h4 class="modifier-id">{{ modifier.modifier_id }}</h4>
                    <el-tag :type="getModifierStatusType(modifier)" size="mini">
                      {{ getModifierStatusText(modifier) }}
                    </el-tag>
                  </div>

                  <div class="modifier-info">
                    <div class="info-row">
                      <span class="label">Name:</span>
                      <span class="value">{{ modifier.modifier_name }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Revenue:</span>
                      <span class="value amount">{{ formatCurrency(modifier.total_revenue) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Quantity:</span>
                      <span class="value">{{ modifier.total_quantity.toLocaleString() }} units</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Orders:</span>
                      <span class="value">{{ modifier.total_orders.toLocaleString() }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Avg Price:</span>
                      <span class="value">{{ formatCurrency(modifier.average_price) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">Items:</span>
                      <span class="value">{{ modifier.unique_items.toLocaleString() }}</span>
                    </div>
                  </div>

                  <div class="modifier-footer">
                    <span class="growth" :class="modifier.growth_rate > 0 ? 'positive' : 'negative'">
                      <i :class="modifier.growth_rate > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(modifier.growth_rate) }}%
                    </span>
                    <div class="actions">
                      <el-button size="mini" type="primary" icon="el-icon-view" @click="viewModifierDetails(modifier)" />
                      <el-button size="mini" type="success" icon="el-icon-chart" @click="viewModifierTrends(modifier)" />
                    </div>
                  </div>
                </el-card>
              </el-col>
            </el-row>
          </div>
        </el-card>
      </div>
    </div>

    <!-- Modifier Details Dialog -->
    <el-dialog
      title="Modifier Details"
      :visible.sync="modifierDetailsVisible"
      width="70%"
      :before-close="handleCloseDialog"
    >
      <div v-if="selectedModifier" class="modifier-details">
        <el-row :gutter="20">
          <el-col :span="12">
            <h4>Modifier Information</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Modifier ID">{{ selectedModifier.modifier_id }}</el-descriptions-item>
              <el-descriptions-item label="Modifier Name">{{ selectedModifier.modifier_name }}</el-descriptions-item>
              <el-descriptions-item label="Average Price">{{ formatCurrency(selectedModifier.average_price) }}</el-descriptions-item>
              <el-descriptions-item label="Unique Items">{{ selectedModifier.unique_items.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Unique Employees">{{ selectedModifier.unique_employees.toLocaleString() }}</el-descriptions-item>
            </el-descriptions>
          </el-col>

          <el-col :span="12">
            <h4>Performance Metrics</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Total Revenue">{{ formatCurrency(selectedModifier.total_revenue) }}</el-descriptions-item>
              <el-descriptions-item label="Total Quantity">{{ selectedModifier.total_quantity.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Total Orders">{{ selectedModifier.total_orders.toLocaleString() }}</el-descriptions-item>
              <el-descriptions-item label="Revenue Share">{{ selectedModifier.revenue_share.toFixed(1) }}%</el-descriptions-item>
              <el-descriptions-item label="Growth Rate">{{ selectedModifier.growth_rate }}%</el-descriptions-item>
            </el-descriptions>
          </el-col>
        </el-row>

        <el-divider content-position="left">Performance Analysis</el-divider>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-card>
              <div slot="header">
                <h5>Revenue per Order</h5>
              </div>
              <div class="metric-value">{{ formatCurrency(selectedModifier.revenue_per_order) }}</div>
            </el-card>
          </el-col>
          <el-col :span="8">
            <el-card>
              <div slot="header">
                <h5>Quantity per Order</h5>
              </div>
              <div class="metric-value">{{ selectedModifier.quantity_per_order.toFixed(1) }}</div>
            </el-card>
          </el-col>
          <el-col :span="8">
            <el-card>
              <div slot="header">
                <h5>Unique Customers</h5>
              </div>
              <div class="metric-value">{{ selectedModifier.unique_customers.toLocaleString() }}</div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="modifierDetailsVisible = false">Close</el-button>
        <el-button type="primary" @click="viewModifierTrends(selectedModifier)">View Trends</el-button>
        <el-button type="success" @click="exportModifierReport(selectedModifier)">Export Report</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import BarChart from '@/components/_Partials/Charts/BarChart.vue'
import DoughnutChart from '@/components/_Partials/Charts/DoughnutChart.vue'
import LineChart from '@/components/_Partials/Charts/LineChart.vue'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'SalesByModifier',
  components: {
    BarChart,
    DoughnutChart,
    LineChart
  },
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      loading: false,
      exporting: false,
      viewMode: 'table',
      modifierDetailsVisible: false,
      selectedModifier: null,
      selectedModifiers: [],
      chartView: 'revenue',
      showPercentage: false,
      trendPeriod: 'monthly',
      modifierData: [],
      trendsData: [],
      trendsPagination: null,
      modifierPagination: null,
      modifiers: [],
      items: [],
      users: [],
      customers: [],
      paymentMethods: [],
      filters: {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        modifier_ids: [],
        item_ids: [],
        user_id: [],
        customer_id: [],
        payment_method_id: [],
        analysis_type: 'revenue',
        sort_by: 'revenue_desc'
      },
      pagination: {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1
      },
      dateShortcuts: [{
        text: 'Last 7 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 6)
          return [start, end]
        }
      }, {
        text: 'Last 30 days',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 29)
          return [start, end]
        }
      }, {
        text: 'Last 3 months',
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setMonth(start.getMonth() - 3)
          return [start, end]
        }
      }, {
        text: 'This year',
        value: () => {
          const end = new Date()
          const start = new Date(new Date().getFullYear(), 0, 1)
          return [start, end]
        }
      }]
    }
  },
  computed: {
    summaryStats () {
      const totalRevenue = this.modifierData.reduce((sum, modifier) => sum + modifier.total_revenue, 0)
      const totalQuantity = this.modifierData.reduce((sum, modifier) => sum + modifier.total_quantity, 0)
      const totalOrders = this.modifierData.reduce((sum, modifier) => sum + modifier.total_orders, 0)
      const avgGrowth = this.modifierData.length > 0
        ? this.modifierData.reduce((sum, modifier) => sum + modifier.growth_rate, 0) / this.modifierData.length
        : 0

      return [
        {
          title: 'Total Revenue',
          value: this.formatCurrency(totalRevenue),
          trend: 12.5,
          icon: 'mdi mdi-currency-fra'
        },
        {
          title: 'Total Quantity',
          value: totalQuantity.toLocaleString(),
          trend: 8.3,
          icon: 'mdi mdi-package-variant'
        },
        {
          title: 'Total Orders',
          value: totalOrders.toLocaleString(),
          trend: 5.2,
          icon: 'mdi mdi-receipt'
        },
        {
          title: 'Avg Growth',
          value: avgGrowth.toFixed(1) + '%',
          trend: avgGrowth,
          icon: 'mdi mdi-trending-up'
        }
      ]
    },
    topModifiers () {
      return this.modifierData.slice(0, 5)
    },
    modifierChartData () {
      const labels = this.modifierData.slice(0, 10).map(modifier => modifier.modifier_name)
      const data = this.modifierData.slice(0, 10).map(modifier => {
        switch (this.chartView) {
          case 'revenue':
            return modifier.total_revenue
          case 'quantity':
            return modifier.total_quantity
          case 'orders':
            return modifier.total_orders
          default:
            return modifier.total_revenue
        }
      })

      return {
        labels: labels,
        datasets: [{
          label: this.chartView.charAt(0).toUpperCase() + this.chartView.slice(1),
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: this.getChartColors(data.length),
          borderWidth: 1
        }]
      }
    },
    modifierChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                if (this.chartView === 'revenue') {
                  return this.formatCurrency(value)
                }
                return value.toLocaleString()
              }.bind(this)
            }
          }
        }
      }
    },
    distributionChartData () {
      const labels = this.modifierData.slice(0, 8).map(modifier => modifier.modifier_name)
      const data = this.modifierData.slice(0, 8).map(modifier => {
        if (this.showPercentage) {
          const total = this.modifierData.reduce((sum, m) => sum + m.total_revenue, 0)
          return ((modifier.total_revenue / total) * 100).toFixed(1)
        }
        return modifier.total_revenue
      })

      return {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: this.getChartColors(data.length),
          borderColor: '#fff',
          borderWidth: 2
        }]
      }
    },
    distributionChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          }
        }
      }
    },
    trendsChartData () {
      return {
        labels: this.trendsData.map(item => this.formatDate(item.date)),
        datasets: this.modifierData.slice(0, 5).map((modifier, index) => ({
          label: modifier.modifier_name,
          data: this.trendsData.map(period =>
            period.modifiers?.find(m => m.modifier_id === modifier.modifier_id)?.revenue || 0
          ),
          borderColor: this.getChartColors(5)[index],
          backgroundColor: this.getChartColors(5)[index] + '20',
          tension: 0.4
        }))
      }
    },
    trendsChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top'
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      }
    }
  },
  mounted () {
    this.loadInitialData()
  },
  methods: {
    getDefaultStartDate () {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },
    getDefaultEndDate () {
      return new Date().toISOString().split('T')[0]
    },
    handleDateChange (dates) {
      if (dates && dates.length === 2) {
        this.filters.start_date = dates[0]
        this.filters.end_date = dates[1]
        this.pagination.current_page = 1
        this.loadModifierData()
      }
    },
    resetFilters () {
      this.filters = {
        dateRange: [this.getDefaultStartDate(), this.getDefaultEndDate()],
        modifier_ids: [],
        item_ids: [],
        user_id: [],
        customer_id: [],
        payment_method_id: [],
        analysis_type: 'revenue',
        sort_by: 'revenue_desc'
      }
      this.pagination.current_page = 1
      this.loadModifierData()
    },
    async loadInitialData () {
      try {
        await Promise.all([
          this.loadModifiers(),
          this.loadItems(),
          this.loadUsers(),
          this.loadCustomers(),
          this.loadPaymentMethods()
        ])
        await this.loadModifierData()
      } catch (error) {
        this.handleError(error, 'Failed to load initial data')
      }
    },
    async loadModifierData () {
      this.loading = true
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        }

        const response = await this.$reportService.getSalesByModifier(params)
        if (response.status === 'success') {
          this.modifierData = response.data
          this.pagination = {
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          }
        }
      } catch (error) {
        this.handleError(error, 'Failed to load modifier data')
      } finally {
        this.loading = false
      }
    },
    async loadTrendsData () {
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          period: this.trendPeriod,
          per_page: 1000,
          page: 1
        }

        const response = await this.$reportService.getModifierTrends(params)
        if (response.status === 'success') {
          this.trendsData = response.data
          if (response.pagination) {
            this.trendsPagination = response.pagination
          }
        }
      } catch (error) {
        console.error('Error loading trends data:', error)
      }
    },
    async loadModifiers () {
      try {
        this.modifiers = await this.$modifierService.getModifiers()
      } catch (error) {
        console.error('Error loading modifiers:', error)
      }
    },
    async loadItems () {
      try {
        this.items = await this.$itemService.getItems()
      } catch (error) {
        console.error('Error loading items:', error)
      }
    },
    async loadUsers () {
      try {
        this.users = await this.$userService.getUsers()
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },
    async loadCustomers () {
      try {
        this.customers = await this.$customerService.getCustomers()
      } catch (error) {
        console.error('Error loading customers:', error)
      }
    },
    async loadPaymentMethods () {
      try {
        this.paymentMethods = await this.$paymentMethodService.getMethods()
      } catch (error) {
        console.error('Error loading payment methods:', error)
      }
    },
    async exportData () {
      this.exporting = true
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const params = {
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          dl: 'pdf'
        }

        const response = await this.$reportService.getSalesByModifier(params)
        if (response.url) {
          window.open(response.url, '_blank')
        }
      } catch (error) {
        this.handleError(error, 'Failed to export data')
      } finally {
        this.exporting = false
      }
    },
    updateChartData () {
      // Chart data will be updated automatically through computed properties
    },
    handleSortChange ({ column, prop, order }) {
      // Handle table sorting
      this.loadModifierData()
    },
    handleSelectionChange (selection) {
      this.selectedModifiers = selection
    },
    handleSizeChange (size) {
      this.pagination.per_page = size
      this.loadModifierData()
    },
    handleCurrentChange (page) {
      this.pagination.current_page = page
      this.loadModifierData()
    },
    toggleViewMode () {
      this.viewMode = this.viewMode === 'table' ? 'grid' : 'table'
    },
    viewModifierDetails (modifier) {
      this.selectedModifier = modifier
      this.modifierDetailsVisible = true
    },
    viewModifierTrends (modifier) {
      // Navigate to modifier trends or open in modal
      console.log('Viewing trends for modifier:', modifier.modifier_name)
    },
    exportModifierReport (modifier) {
      // Implement modifier-specific report export
      console.log('Exporting report for modifier:', modifier.modifier_name)
    },
    handleCloseDialog () {
      this.modifierDetailsVisible = false
      this.selectedModifier = null
    },
    getModifierStatusType (modifier) {
      if (modifier.growth_rate > 10) return 'success'
      if (modifier.growth_rate > 0) return 'warning'
      return 'danger'
    },
    getModifierStatusText (modifier) {
      if (modifier.growth_rate > 10) return 'High Growth'
      if (modifier.growth_rate > 0) return 'Growing'
      return 'Declining'
    },
    getProgressColor (index) {
      const colors = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399']
      return colors[index % colors.length]
    },
    getChartColors (count) {
      const colors = [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#9C27B0', '#FF9800', '#795548', '#607D8B', '#FF5722'
      ]
      return colors.slice(0, count)
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'XAF'
      }).format(value)
    },
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.sales-by-modifier {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stat-info {
  flex: 1;
}

.stat-title {
  color: #606266;
  font-size: 14px;
  margin: 0 0 8px 0;
}

.stat-value {
  color: #303133;
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 8px 0;
}

.stat-comparison {
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend {
  font-size: 12px;
  font-weight: bold;
}

.trend.positive {
  color: #67C23A;
}

.trend.negative {
  color: #F56C6C;
}

.comparison-text {
  font-size: 12px;
  color: #909399;
}

.stat-icon {
  font-size: 32px;
  color: #409EFF;
  opacity: 0.8;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chart-container {
  position: relative;
  height: 300px;
}

.top-modifiers-list {
  max-height: 300px;
  overflow-y: auto;
}

.modifier-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #EBEEF5;
}

.modifier-item:last-child {
  border-bottom: none;
}

.modifier-rank {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #409EFF;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-right: 12px;
}

.modifier-info {
  flex: 1;
  margin-right: 12px;
}

.modifier-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.modifier-stats {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.modifier-trend {
  text-align: right;
  font-size: 12px;
}

.growth {
  font-weight: bold;
}

.growth.positive {
  color: #67C23A;
}

.growth.negative {
  color: #F56C6C;
}

.grid-view {
  margin-top: 20px;
}

.modifier-card {
  transition: all 0.3s ease;
}

.modifier-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.modifier-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
</style>
